import React from 'react'
import {
  range, times, isNil, isObject, startCase
} from 'lodash'
import { DetailList } from '@ideon-api/ideon-ui'

// PD-30615 MR3: the CVT plan card's benefit blocks. Each `cvtTableRow` returns
// one self-contained block -- a name header plus its tiers stacked one per
// line -- for a caller (PlanBenefitsComponent) to place into a column or
// matrix grid. No legacy `box`/`flex-box` classes: this markup lives inside
// the panel's `.ideon-ui-scope` wrapper (ADR 0011), so it is ideon-ui/utility
// classes only.
const NAME_CLASS = 'text-sm font-medium text-ideon-gray-black dark:text-ideon-gray-30'
const VALUE_CLASS = 'text-sm text-ideon-gray-70 dark:text-ideon-gray-60'

export const singleTierType = {
  options(additionalTiersCount) { return ['Benefit'] },
  headers(additionalTiersCount, includeCoverage) {
    return ['Benefit', 'Limit']
  },
  initialBenefitForName: (name) => ({
    name,
    value: name,
    tiers: [
      { option: 'Benefit', value: '' }
    ]
  }),
  objectFieldName: (fieldName) => {
    let objectFieldName = fieldName

    if (fieldName.includes('_limit') || fieldName.endsWith('_benefit')) {
      objectFieldName = `benefits.${fieldName}`
      objectFieldName = objectFieldName.replace('_benefit', '.benefit')
      objectFieldName = objectFieldName.replace('_limit', '.limit')
    }

    return objectFieldName
  },
  benefitsFields: (includeCoverageTier, maxTiers) => {
    return [
      { key: 'benefit', label: 'Benefit' },
      { key: 'limit', label: 'Limit' }
    ]
  },
  cvtTableRow: (planId, fieldName, benefitName, benefitValue) => {
    const testId = `benefit-${planId}-${fieldName}`

    return (
      <div key={testId} data-testid={testId} className="mb-3">
        <div className={NAME_CLASS} data-testid={`${testId}-name`}>{benefitName}</div>
        <DetailList.Root>
          <DetailList.Item
            key="benefit"
            label="Benefit"
            value={sanitizeField(benefitValue, 'benefit')}
            data-testid={`${testId}-benefit`}
          />
          <DetailList.Item
            key="limit"
            label="Limit"
            value={sanitizeField(benefitValue, 'limit')}
            data-testid={`${testId}-limit`}
          />
        </DetailList.Root>
      </div>
    )
  }
}

export const networkTierType = {
  options(additionalTiersCount) {
    const tierOptions = []
    tierOptions.push('In Network')

    times(additionalTiersCount).forEach((index) => {
      tierOptions.push(`In Network Tier ${index + 2}`)
    })

    tierOptions.push('Out of Network')
    return tierOptions
  },
  headers(additionalTiersCount, includeCoverageTier) {
    const tiers = times(additionalTiersCount).map((index) => `In Network Tier ${index + 2}`)
    let tierHeader = ['In Network'].concat(tiers, 'Out of Network', 'Limit')
    if (includeCoverageTier) {
      tierHeader = tierHeader.concat('Coverage Tier')
    }
    return tierHeader
  },
  initialBenefitForName: (name) => ({
    name,
    value: name,
    tiers: [
      { option: 'In Network', value: '' },
      { option: 'Out of Network', value: '' }
    ]
  }),
  objectFieldName: (fieldName) => {
    let objectFieldName = fieldName

    if (
      fieldName.includes('_limit')
      || fieldName.includes('_in_network')
      || fieldName.includes('_out_of_network')
      || fieldName.includes('_coverage_tier')
    ) {
      objectFieldName = `benefits.${fieldName}`
      objectFieldName = objectFieldName.replace('_limit', '.limit')
      objectFieldName = objectFieldName.replace('_coverage_tier', '.coverage_tier')
      objectFieldName = objectFieldName.replace('_in_network', '.in_network')
      objectFieldName = objectFieldName.replace('_out_of_network', '.out_of_network')
    }

    return objectFieldName
  },
  benefitsFields: (includeCoverageTier, maxTiers) => {
    let fields = [{ key: 'in_network', label: 'In Network' }]

    fields = fields.concat(
      range(2, Math.max(2, maxTiers + 1))
        .map((v) => ({ key: `in_network_tier_${v}`, label: `In Network Tier ${v}` }))
    )

    fields.push({ key: 'out_of_network', label: 'Out of Network' })
    fields.push({ key: 'limit', label: 'Limit' })

    if (includeCoverageTier) {
      fields.push({ key: 'coverage_tier', label: 'Coverage Tier' })
    }

    return fields
  },
  cvtTableRow: (planId, fieldName, benefitName, benefitValue) => {
    const testId = `benefit-${planId}-${fieldName}`

    if (isObject(benefitValue)) {
      const pairs = tierPairs(benefitValue)

      return (
        <div key={testId} data-testid={testId} className="mb-3">
          <div className={NAME_CLASS} data-testid={`${testId}-name`}>{benefitName}</div>
          {pairs.length > 0 && (
            <DetailList.Root>
              {pairs.map(({ key, label, value }) => (
                <DetailList.Item key={key} label={label} value={value} data-testid={`${testId}-${key}`} />
              ))}
            </DetailList.Root>
          )}
        </div>
      )
    }

    return (
      <div key={testId} data-testid={testId} className="mb-3">
        <div className={NAME_CLASS} data-testid={`${testId}-name`}>{benefitName}</div>
        <div className={VALUE_CLASS} data-testid={`${testId}-value`}>
          {isNil(benefitValue) ? '–' : benefitValue}
        </div>
      </div>
    )
  }
}

export const occurrenceTierType = {
  options(additionalTiersCount) { return ['First Occurrence', 'Second Occurrence'] },
  headers(additionalTiersCount, includeCoverage) {
    return ['First Occurrence', 'Second Occurrence', 'Limit']
  },
  initialBenefitForName: (name) => ({
    name,
    value: name,
    tiers: [
      { option: 'First Occurrence', value: '' },
      { option: 'Second Occurrence', value: '' }
    ]
  }),
  objectFieldName: (fieldName) => {
    let objectFieldName = fieldName

    if (fieldName.includes('_limit')
        || fieldName.endsWith('_first_occurrence')
        || fieldName.endsWith('_second_occurrence')) {
      objectFieldName = `benefits.${fieldName}`
      objectFieldName = objectFieldName.replace('_first_occurrence', '.first_occurrence')
      objectFieldName = objectFieldName.replace('_second_occurrence', '.second_occurrence')
      objectFieldName = objectFieldName.replace('_limit', '.limit')
    }

    return objectFieldName
  },
  benefitsFields: (includeCoverageTier, maxTiers) => {
    return [
      { key: 'first_occurrence', label: 'First Occurrence' },
      { key: 'second_occurrence', label: 'Second Occurrence' },
      { key: 'limit', label: 'Limit' }
    ]
  },
  cvtTableRow: (planId, fieldName, benefitName, benefitValue) => {
    const testId = `benefit-${planId}-${fieldName}`

    return (
      <div key={testId} data-testid={testId} className="mb-3">
        <div className={NAME_CLASS} data-testid={`${testId}-name`}>{benefitName}</div>
        <DetailList.Root>
          <DetailList.Item
            key="first_occurrence"
            label="1st occurrence"
            value={sanitizeField(benefitValue, 'first_occurrence')}
            data-testid={`${testId}-first-occurrence`}
          />
          <DetailList.Item
            key="second_occurrence"
            label="2nd occurrence"
            value={sanitizeField(benefitValue, 'second_occurrence')}
            data-testid={`${testId}-second-occurrence`}
          />
          <DetailList.Item
            key="limit"
            label="Limit"
            value={sanitizeField(benefitValue, 'limit')}
            data-testid={`${testId}-limit`}
          />
        </DetailList.Root>
      </div>
    )
  }
}

function sanitizeField(field, fieldType) {
  if (isNil(field) || isNil(field[fieldType]) || field[fieldType] === '') { return '–' }

  return field[fieldType]
}

function hasRenderableValue(value, key) {
  // `0` is a real benefit value, so this deliberately is not a truthiness check.
  return !isNil(value[key]) && value[key] !== ''
}

// The wire carries at most `in_network_tier_10` (vericred's
// `Vericred::Benefits::TiersConverter::ORDERED_TIERS`). An 11th tier would
// still render -- the unrecognised-key loop below catches it -- but after
// `coverage_tier` rather than in sequence, so raise this if the domain does.
const MAX_NETWORK_TIER = 10

// PD-30615 MR3: was `stringifyObject`, which joined these into one
// `' / '`-separated string for a single value cell. The redesigned CVT plan
// card stacks tiers one per line instead, so this now returns the ordered
// {key, label, value} pairs for the caller to render as separate rows.
function tierPairs(value) {
  const knownFields = networkTierType.benefitsFields(true, MAX_NETWORK_TIER)
  const knownKeys = new Set(knownFields.map(({ key }) => key))
  const pairs = []

  knownFields.forEach(({ key, label }) => {
    if (hasRenderableValue(value, key)) { pairs.push({ key, label, value: value[key] }) }
  })

  // Unrecognised keys (e.g. a future API field) still render, appended after
  // the known ones, rather than being silently dropped.
  Object.keys(value).forEach((key) => {
    if (knownKeys.has(key)) { return }
    if (hasRenderableValue(value, key)) { pairs.push({ key, label: startCase(key), value: value[key] }) }
  })

  return pairs
}

function tierTypeFactory(tierString) {
  switch (tierString) {
  case 'single_tier': return singleTierType
  case 'occurrence_tier': return occurrenceTierType
  default: return networkTierType
  }
}

export default function updateGroupTier(benefitsGroups) {
  benefitsGroups.forEach((group) => {
    group.tier = tierTypeFactory(group.tier) // eslint-disable-line no-param-reassign
  })
}

